import { insertEntityAfterParent } from './chat-entity-insertion.helper'
import type { ToolCallEntity, ToolStatus } from './chat-message-entity.interface'
import type { ChatState } from './chat-state.interface'

export interface AddToolCallEntityPayload {
  conversationId: string
  parentEventId: string
  toolCallId: string
  toolName: string
  toolArgs: Record<string, unknown>
  agentName?: string
  agentLabel?: string
  toolCategory?: string
  threadId?: string
  parentThreadId?: string | null
  startedAt: string
}

export interface UpdateToolCallEntityPayload {
  parentEventId: string
  toolCallId: string
  status: ToolStatus
  output?: string
  errorMessage?: string
  htmlResultKey?: string
  finishedAt?: string
}

export interface UpdateToolCallEntityProgressPayload {
  parentEventId: string
  toolCallId: string
  message: string
  percentage: number
}

const buildToolCallEntityId = (parentEventId: string, toolCallId: string): string =>
  `${parentEventId}::tc::${toolCallId}`

export const handleAddToolCallEntity = (
  state: ChatState,
  payload: AddToolCallEntityPayload
): void => {
  const id = buildToolCallEntityId(payload.parentEventId, payload.toolCallId)
  if (state.messageEntities[id]) return

  const displayName = payload.agentLabel ?? payload.agentName ?? 'Assistant'

  const entity: ToolCallEntity = {
    id,
    conversationId: payload.conversationId,
    type: 'tool-call',
    content: '',
    timestamp: payload.startedAt,
    sender: 'assistant',
    participant: { id: payload.agentName ?? 'assistant', name: displayName },
    toolName: payload.toolName,
    friendlyName: payload.toolName,
    toolStatus: 'running',
    toolInput: Object.keys(payload.toolArgs).length
      ? JSON.stringify(payload.toolArgs, null, 2)
      : undefined,
    toolCallId: payload.toolCallId,
    toolSlug: payload.toolName,
    toolCategory: payload.toolCategory,
    toolArgs: payload.toolArgs,
    toolStartedAt: payload.startedAt,
    threadId: payload.threadId,
    parentThreadId: payload.parentThreadId,
    turnStartedAt: payload.startedAt,
    agentLabel: payload.agentLabel,
    isPending: false,
    isStreaming: true,
    streamingContent: '',
  }

  insertEntityAfterParent({
    state,
    conversationId: payload.conversationId,
    parentEventId: payload.parentEventId,
    entity,
  })
}

export const handleUpdateToolCallEntity = (
  state: ChatState,
  payload: UpdateToolCallEntityPayload
): void => {
  const id = buildToolCallEntityId(payload.parentEventId, payload.toolCallId)
  const entity = state.messageEntities[id]
  if (entity?.type !== 'tool-call') return

  entity.toolStatus = payload.status
  entity.toolOutput = payload.output
  entity.toolErrorMessage = payload.errorMessage
  entity.htmlResultKey = payload.htmlResultKey
  entity.toolFinishedAt = payload.finishedAt ?? null
  entity.toolProgressMessage = undefined
  entity.toolProgressPercentage = undefined
  entity.isStreaming = false
}

export const handleUpdateToolCallEntityProgress = (
  state: ChatState,
  payload: UpdateToolCallEntityProgressPayload
): void => {
  const id = buildToolCallEntityId(payload.parentEventId, payload.toolCallId)
  const entity = state.messageEntities[id]
  if (entity?.type !== 'tool-call') return

  entity.toolProgressMessage = payload.message
  entity.toolProgressPercentage = payload.percentage
}
